/**
 * Extracts the file extension from a URL or file path.
 * Handles query parameters, fragments, and edge cases.
 *
 * @param url - The URL or file path to extract extension from
 * @returns The file extension (without the dot) or null if no extension found
 *
 * @example
 * getFileExtension('image.jpg') // 'jpg'
 * getFileExtension('path/to/file.svg?w=100&h=200') // 'svg'
 * getFileExtension('document.pdf#page=1') // 'pdf'
 * getFileExtension('file.name.with.dots.png') // 'png'
 * getFileExtension('no-extension') // null
 * getFileExtension('') // null
 */
export function getFileExtension(url: string): string | null {
  if (!url || typeof url !== 'string') {
    return null
  }

  try {
    // Remove query parameters and fragments
    const cleanUrl = url.split('?')[0]?.split('#')[0]

    if (!cleanUrl) {
      return null
    }

    // Extract filename from path (handle both forward and back slashes)
    const pathParts = cleanUrl.split(/[/\\]/)
    const filename = pathParts.at(-1)

    if (!filename) {
      return null
    }

    // Find the last dot in the filename
    const lastDotIndex = filename.lastIndexOf('.')

    // If no dot found, or dot is at the beginning (hidden file), or at the end
    if (lastDotIndex === -1 || lastDotIndex === 0 || lastDotIndex === filename.length - 1) {
      return null
    }

    const extension = filename.slice(lastDotIndex + 1).toLowerCase()

    // Return null for empty extensions
    return extension || null
  } catch {
    // Handle any unexpected errors (like malformed URLs)
    return null
  }
}

/**
 * Checks if a file has a specific extension.
 *
 * @param url - The URL or file path to check
 * @param extension - The extension to check for (with or without dot)
 * @returns True if the file has the specified extension
 *
 * @example
 * hasFileExtension('image.jpg', 'jpg') // true
 * hasFileExtension('image.jpg', '.jpg') // true
 * hasFileExtension('path/to/file.svg?w=100', 'svg') // true
 * hasFileExtension('document.pdf', 'png') // false
 */
export function hasFileExtension(url: string, extension: string): boolean {
  const fileExt = getFileExtension(url)
  const targetExt = extension.startsWith('.')
    ? extension.slice(1).toLowerCase()
    : extension.toLowerCase()

  return fileExt === targetExt
}
